/**
 * The CompanyDetails-Component displays a specific company as well as all the associated projects
 */

import React, { useContext } from "react";
import dayjs from "dayjs";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Typography,
  useTheme,
  styled,
  Button,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { AuthContext } from "../../context/auth-context/AuthContext";
import useCompanyDetails from "../../hooks/companies/useCompanyDetails";
import { ProjectWithContactDto } from "../../types/projectTypes";
import { formatDate } from "../../utils/dateUtils";
import ProjectChip from "../../components/projects/projectCard/ProjectChip";

/**
 * Depicts a specific company as well as a table with all the associated projects
 */
const CompanyDetails: React.FunctionComponent = () => {
  const theme = useTheme();

  /**
   * Function which provides the styles of the CompanyDetails
   */
  const styles = {
    button: {
      marginTop: "20px",
      marginBottom: "20px",
      border: "0",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        color: "black",
      },
    },
    tableContainer: {
      maxHeight: (window.screen.height - 75) * 0.8,
    },
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  };

  const StyledLink = styled(Link)`
    text-decoration: none;
    color: black;
    &:hover {
      text-decoration: underline;
    }
  `;

  const { id } = useParams();
  const { companyDetails, companyContactPersonNotes } = useCompanyDetails(Number(id));

  const contactPersonNames = companyDetails?.contactPersons?.map((contactPerson) => contactPerson.name).join(", ");

  const getCoreCompetenciesForProject = (project: ProjectWithContactDto) => {
    return project.coreCompetencies.map((competency) => competency.designation).join(", ");
  };

  const getBTForProject = (project: ProjectWithContactDto) => {
    if (project.soldBT !== null && project.soldBT !== undefined) {
      return project.soldBT.toString();
    } else if (project.estimatedProjectBTmin !== null && project.estimatedProjectBTmax !== null) {
      return `${project.estimatedProjectBTmin} - ${project.estimatedProjectBTmax}`;
    }
    return "";
  };

  const { auth } = useContext(AuthContext);
  const hasCrmPermissions = doesPermissionsHaveSomeOf(auth.permissions, [13]);
  const isPLOfSomeProjects = companyDetails?.projects?.some((project) =>
    project.projectMembers.some((member) => member.memberId === auth.userID)
  );

  return (
    <div>
      <Grid container spacing={1} marginBottom={"50px"}>
        {(hasCrmPermissions || isPLOfSomeProjects) && (
          <Grid item>
            <Button sx={styles.button} variant="contained" component={Link} to={`/unternehmen/${id}/bearbeiten`}>
              Kunden bearbeiten
            </Button>
          </Grid>
        )}
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Name:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.name}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Branche:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.industry.description}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Kurzbeschreibung:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.shortDescription}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Adresszusatz:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.addressAdditional}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Straße/Nr:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.street}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">PLZ:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.postalCode}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Ort:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.city}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Website:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.url}</Typography>
          </Grid>
        </Grid>
        {(hasCrmPermissions || isPLOfSomeProjects) && (
          <Grid item container alignItems="center" spacing={0} xs={12}>
            <Grid item sx={{ width: "200px" }}>
              <Typography variant="body1">Wichtige Informationen:</Typography>
            </Grid>
            <Grid item xs>
              <Typography>{companyDetails?.importantInformation}</Typography>
            </Grid>
          </Grid>
        )}
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Kontakt erwünscht:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.contactDesired ? "Ja" : "Nein"}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Geheim:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{companyDetails?.classified ? "Ja" : "Nein"}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Ansprechpartner:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{contactPersonNames}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>Name</TableCell>
              <TableCell sx={styles.tableHeadCell}>Ansprechpartner</TableCell>
              <TableCell sx={styles.tableHeadCell}>Kernkompetenz</TableCell>
              <TableCell sx={styles.tableHeadCell}>Beginn</TableCell>
              <TableCell sx={styles.tableHeadCell}>Ende</TableCell>
              <TableCell sx={styles.tableHeadCell}>BT</TableCell>
              <TableCell sx={styles.tableHeadCell}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companyDetails?.projects?.map((project, index) => (
              <TableRow hover key={index}>
                <TableCell component="th" scope="row">
                  <Typography color="secondary">
                    <StyledLink to={`/projekte/${project.projectId}`}>{`${project.projectName}`}</StyledLink>
                  </Typography>
                </TableCell>
                <TableCell>{project.contactPerson?.name ? project.contactPerson.name : ""}</TableCell>
                <TableCell>{getCoreCompetenciesForProject(project)}</TableCell>
                <TableCell>{formatDate(project.kickoff)}</TableCell>
                <TableCell>{formatDate(project.projectEnd)}</TableCell>
                <TableCell>{getBTForProject(project)}</TableCell>
                <TableCell>
                  <ProjectChip status={project.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="h6" marginTop={"24px"} marginBottom={"8px"}>
        Ansprechpartner-Notizen
      </Typography>
      {/* Read-only table for contact person notes, rendered below the projects table. */}
      <TableContainer component={Paper}>
        <Table aria-label="contact person notes table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>Ansprechpartner</TableCell>
              <TableCell sx={styles.tableHeadCell}>Notiz</TableCell>
              <TableCell sx={styles.tableHeadCell}>Autor + Erstelldatum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Keep the empty state explicit until create/edit is implemented in a later step. */}
            {companyContactPersonNotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>Keine Notizen vorhanden.</TableCell>
              </TableRow>
            ) : (
              companyContactPersonNotes.map((note) => (
                <TableRow hover key={note.contactPersonNoteId}>
                  <TableCell>{note.contactPersonName}</TableCell>
                  <TableCell>{note.note}</TableCell>
                  <TableCell>{`${note.authorName} am ${dayjs(note.createdAt).format("DD.MM.YYYY")}`}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid item container marginBottom={"50px"}></Grid>
    </div>
  );
};

export default CompanyDetails;
